import { World } from 'miniplex'
import type { Entity } from './components'
import { applyPlayerRadius } from './growth'
import { applyFormEyes, attachEyes } from './systems/eyes'
import { initLineage } from './systems/lineage'
import { pickMicrobeSpecies, morphologyFromSpecies } from './species'
import { morphologyFrom } from './creature-recipe'
import { spawnDnaOrb } from './systems/dna-orb'
import { spawnContainers } from './systems/containers'
import { attunedCheckpointId } from './systems/checkpoint'
import { mulberry32 } from './rng'
import { inWater } from './dungeon/depth-field'
import { roomRadius } from './dungeon/embed'
import type { DungeonLayout } from './dungeon/types'
import { T } from './tunables'

export type GameWorld = World<Entity>

export function createWorld(): GameWorld {
  return new World<Entity>()
}

// The world seed + generated layout, threaded from main.ts (persisted per
// save) through seedDemo and the respawn lifecycle. Bare seedDemo(world)
// calls (tests, legacy) fall back to a per-session seed and the open-water
// hub — exactly the pre-B2 world.
export interface WorldCtx {
  seed: number
  layout?: DungeonLayout
}

// Per-session fallback seed for bare seedDemo calls.
const WORLD_SEED = (Date.now() & 0xffffffff) >>> 0

export function getWorldSeed(): number {
  return WORLD_SEED
}

export function seedDemo(world: GameWorld, ctx?: WorldCtx): Entity {
  const layout = ctx?.layout
  const rng = mulberry32(ctx?.seed ?? WORLD_SEED)

  // Starter food around the hub. In the walled world the hub basin is
  // ~5 units — the legacy 0.5–3.5 ring fits, but lobed banks can clip a
  // sample, so reject into water.
  for (let i = 0; i < 25; i++) {
    let x = 0.6
    let y = 0.6
    for (let attempt = 0; attempt < 10; attempt++) {
      const angle = Math.random() * Math.PI * 2
      const r = 0.5 + Math.random() * 3.5
      x = Math.cos(angle) * r
      y = Math.sin(angle) * r
      if (inWater(x, y, 0.1)) break
    }
    world.add({
      position: { x, y },
      velocity: { x: 0, y: 0 },
      wobble: { phase: Math.random() * Math.PI * 2, freq: 1.5 + Math.random() * 1.5, amp: 0.05 },
      renderable: {
        shape: 'microbe',
        radius: 0.05 + Math.random() * 0.04,
        tint: [0.5 + Math.random() * 0.4, 0.7 + Math.random() * 0.3, 1.0],
      },
      // Hub starter food carries species morphology too (grain bodies) —
      // it previously spawned featureless and dodged the shape grammar.
      morphology: morphologyFromSpecies(pickMicrobeSpecies('shallow')),
      wander: {
        impulseTimer: Math.random() * 2,
        noiseSeed: Math.random() * 100,
      },
      consumable: true,
    })
  }
  const PLAYER_BASE_RADIUS = 0.12
  const player: Entity = {
    position: { x: 0, y: 0 },
    velocity: { x: 0, y: 0 },
    wobble: { phase: 0, freq: 2.4, amp: 0.06 },
    renderable: { shape: 'player', radius: PLAYER_BASE_RADIUS, tint: [0.6, 0.95, 1.0] },
    player: true,
    dna: 0,
    health: { current: 5, max: 5 },
    dash: { cooldownRemaining: 0, activeRemaining: 0, impulse: { x: 0, y: 0 } },
    shield: { cooldownRemaining: 0, activeRemaining: 0 },
    engulf: { cooldownRemaining: 0, activeRemaining: 0 },
    portal: { cooldownRemaining: 0 },
    baseRadius: PLAYER_BASE_RADIUS,
    // Divergent evolution: a random starting nudge; behavior bends it over
    // tiers 1→2, and it locks into a critter at the tier-3 speciation.
    lineage: initLineage(),
  }
  applyPlayerRadius(player)
  // Socket offsets/radii are in body-radius units; eyesSystem converts to world.
  // Fresh lineage has no form yet, so this attaches the amoeba defaults.
  applyFormEyes(player)
  world.add(player)

  // Mother Cell NPC — slightly off origin so player doesn't overlap on spawn.
  // Bigger than the player, warm rose tint, slow breathing wobble; asymmetric
  // eyes (one large — reads as reading-glasses without a new shader).
  const mother: Entity = {
    position: { x: 0.6, y: 0 },
    velocity: { x: 0, y: 0 },
    wobble: { phase: 0, freq: 0.45, amp: 0.03 },
    renderable: { shape: 'storekeeper', radius: 0.24, tint: [0.93, 0.66, 0.5] },
    // FEAT_PEAR: bottom-heavy silhouette + hem frill (visual direction). Seed
    // pinned so the mother's silhouette is stable across sessions.
    morphology: morphologyFrom({ body: 'pear' }, 0.5),
    npc: { kind: 'storekeeper', dialogIndex: 0, approachCooldown: 0, dialogActive: false, contactBlocked: false },
  }
  attachEyes(mother, [
    { offset: { x: -0.3, y: 0.22 }, radius: 0.2 },
    { offset: { x:  0.32, y: 0.26 }, radius: 0.4 },
  ])
  world.add(mother)

  // Daughter-cell checkpoints (bead monorail-s8k): small budded-off Mother
  // cells at biome-band entrances. Attunement is module state in
  // systems/checkpoint.ts (survives world.clear()); `attuned` here just
  // reflects it at spawn so a restored save shows the right cell warm from
  // frame one. Never carries npc/consumable/health — those would corrupt
  // portal-home, mother homing, the orb beacon, and shop reach.
  const CHECKPOINT_TINT: [number, number, number] = [0.95, 0.78, 0.75]
  function spawnCheckpointCell(id: string, x: number, y: number, seed: number): void {
    const cell: Entity = {
      position: { x, y },
      velocity: { x: 0, y: 0 },
      // Same slow breathing as the Mother, scaled down for a smaller body.
      wobble: { phase: 0, freq: 0.45, amp: 0.02 },
      renderable: { shape: 'storekeeper', radius: T.checkpoint.cellRadius, tint: CHECKPOINT_TINT },
      morphology: morphologyFrom({ body: 'pear' }, seed),
      checkpoint: { id, attuned: id === attunedCheckpointId() },
    }
    attachEyes(cell, [
      { offset: { x: -0.3, y: 0.22 }, radius: 0.2 },
      { offset: { x: 0.3, y: 0.22 }, radius: 0.2 },
    ])
    world.add(cell)
  }

  if (layout?.nodeDepth) {
    // Walled world: the shallowest room whose depth crosses each band
    // boundary — deterministic per seed, ids stable across reseeds of the
    // same save. Skip a band's cell gracefully if nothing qualifies.
    const nodeDepth = layout.nodeDepth
    const roomNodes = layout.graph.nodes.filter(n => n.role === 'room')
    const shallowestPast = (threshold: number) =>
      roomNodes
        .filter(n => nodeDepth[n.id] >= threshold)
        .sort((a, b) => nodeDepth[a.id] - nodeDepth[b.id])[0]

    const deepEntrance = shallowestPast(T.biomes.fullDepthDist)
    if (deepEntrance) {
      const p = layout.pos[deepEntrance.id]
      spawnCheckpointCell(`checkpoint:n${deepEntrance.id}`, p.x, p.y, 0.35)
    }
    const mushroomEntrance = shallowestPast(T.biomes.mushroomInner)
    if (mushroomEntrance) {
      const p = layout.pos[mushroomEntrance.id]
      spawnCheckpointCell(`checkpoint:n${mushroomEntrance.id}`, p.x, p.y, 0.65)
    }
  } else {
    // Open water: fixed spots on the +x axis at the same band radii,
    // nudged into water if the direct spot happens to miss (no-op today —
    // everything is water without an active layout — but keeps the spawn
    // safe if that ever changes).
    const spots: Array<{ id: string; x: number; y: number; seed: number }> = [
      { id: 'checkpoint:deep', x: T.biomes.fullDepthDist, y: 0, seed: 0.35 },
      { id: 'checkpoint:mushroom', x: T.biomes.mushroomInner, y: 0, seed: 0.65 },
    ]
    for (const spot of spots) {
      let x = spot.x
      let y = spot.y
      if (!inWater(x, y, 0.15)) {
        for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1], [0.5, 0.5]] as const) {
          if (inWater(x + dx, y + dy, 0.15)) { x += dx; y += dy; break }
        }
      }
      spawnCheckpointCell(spot.id, x, y, spot.seed)
    }
  }

  // Three navigation landmarks. In the walled world they anchor generated
  // basins at the same depth spread the old radial placements had; in open
  // water they keep their hand-placed spots.
  const LANDMARK_KINDS: Array<{ kind: 'reticle' | 'droplet' | 'duck'; tint: [number, number, number] }> = [
    { kind: 'reticle', tint: [0.6, 0.8, 1.0] },
    { kind: 'droplet', tint: [0.4, 0.7, 0.9] },
    { kind: 'duck',    tint: [1.0, 0.88, 0.3] },
  ]
  const LEGACY_LANDMARK_POS = [
    { x: 8,   y: 0 },
    { x: -12, y: 9 },
    { x: 18,  y: -19 },
  ]
  const landmarkPositions = layout?.landmarkNodes
    ? layout.landmarkNodes.map(id => ({ x: layout.pos[id].x, y: layout.pos[id].y }))
    : LEGACY_LANDMARK_POS
  for (let i = 0; i < landmarkPositions.length; i++) {
    world.add({
      position: { x: landmarkPositions[i].x, y: landmarkPositions[i].y },
      renderable: { shape: 'landmark', radius: 0.7, tint: LANDMARK_KINDS[i].tint },
      landmark: { kind: LANDMARK_KINDS[i].kind },
    })
  }

  // Phase 6c → monorail-edu — one guaranteed large DNA cache at each landmark,
  // replacing the retired mutation-orb drop. The cache is a worth-the-trip
  // reward for reaching a navigation anchor, and (as non-own DNA) it credits
  // the growth-spurt meter on pickup.
  for (const lm of landmarkPositions) {
    // Offset the cache east of the landmark so it doesn't occlude the
    // silhouette — unless that lands in a bank; then walk the compass.
    let ox = lm.x + 1.0
    let oy = lm.y
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1], [0.5, 0]] as const) {
      if (inWater(lm.x + dx, lm.y + dy, 0.15)) {
        ox = lm.x + dx
        oy = lm.y + dy
        break
      }
    }
    spawnDnaOrb(world, ox, oy, 40)
  }

  // Hazards just outside the hub, so a fresh player meets them shortly
  // after leaving home without bumping into one at spawn.
  type HazardSpec = { kind: 'spike' | 'toxic' | 'current'; build: (x: number, y: number, rng: () => number) => Entity }
  const HAZARD_KINDS: HazardSpec[] = [
    { kind: 'spike', build: (x, y) => ({
        position: { x, y },
        hazard: { kind: 'spike', damage: 1 },
        renderable: { shape: 'spike', radius: 0.14, tint: [0.9, 0.25, 0.3] },
      }) },
    { kind: 'spike', build: (x, y) => ({
        position: { x, y },
        hazard: { kind: 'spike', damage: 1 },
        renderable: { shape: 'spike', radius: 0.14, tint: [0.9, 0.25, 0.3] },
      }) },
    { kind: 'toxic', build: (x, y) => ({
        position: { x, y },
        hazard: { kind: 'toxic', damage: 0.5, tickInterval: 0.25, tickTimer: 0 },
        renderable: { shape: 'toxic', radius: 0.7, tint: [0.3, 0.9, 0.4] },
      }) },
    { kind: 'current', build: (x, y, rng) => {
        const dir = rng() * Math.PI * 2
        return {
          position: { x, y },
          hazard: { kind: 'current', impulse: { x: Math.cos(dir) * 0.6, y: Math.sin(dir) * 0.6 } },
          renderable: { shape: 'current', radius: 0.9, tint: [0.35, 0.55, 1.0] },
        }
      } },
    { kind: 'spike', build: (x, y) => ({
        position: { x, y },
        hazard: { kind: 'spike', damage: 1 },
        renderable: { shape: 'spike', radius: 0.14, tint: [0.9, 0.25, 0.3] },
      }) },
    { kind: 'toxic', build: (x, y) => ({
        position: { x, y },
        hazard: { kind: 'toxic', damage: 0.5, tickInterval: 0.25, tickTimer: 0 },
        renderable: { shape: 'toxic', radius: 0.7, tint: [0.3, 0.9, 0.4] },
      }) },
  ]
  if (layout?.nodeDepth) {
    // Walled world: scatter the hazards inside first/second-band basins —
    // "shortly after leaving the hub" measured in halls, not radii. Kept off
    // basin centers so they read as furniture, not ambushes on the swim line.
    const earlyRooms = layout.graph.nodes.filter(n => (n.band === 1 || n.band === 2) && n.role === 'room')
    for (const spec of HAZARD_KINDS) {
      for (let attempt = 0; attempt < 40; attempt++) {
        const room = earlyRooms[Math.floor(rng() * earlyRooms.length)]
        const rp = layout.pos[room.id]
        const ang = rng() * Math.PI * 2
        const dist = roomRadius(room.sizeHint) * (0.35 + rng() * 0.5)
        const x = rp.x + Math.cos(ang) * dist
        const y = rp.y + Math.sin(ang) * dist
        if (!inWater(x, y, 0.3)) continue
        world.add(spec.build(x, y, rng))
        break
      }
    }
  } else {
    // Open water: the legacy ring. Distances 5.5–8 keep them in the
    // early-depth band (HUB_RADIUS=4, FULL_DEPTH_DIST=22).
    const hazardCount = HAZARD_KINDS.length
    for (let i = 0; i < hazardCount; i++) {
      const baseAngle = (i / hazardCount) * Math.PI * 2
      const angle = baseAngle + (rng() - 0.5) * (Math.PI / hazardCount)
      const dist = T.biomes.hubRadius + 1.5 + rng() * 2.5  // 5.5 – 8 from origin
      const x = Math.cos(angle) * dist
      const y = Math.sin(angle) * dist
      world.add(HAZARD_KINDS[i].build(x, y, rng))
    }
  }

  // Breakable containers (B6) — deterministic per-seed furniture in the
  // walled world. Session-broken ones sit out until the next boot
  // (spawnContainers consults its module-scope broken set).
  if (layout) spawnContainers(world, layout, ctx?.seed ?? WORLD_SEED)

  return player
}
